/**
 * Error Boundary Components for ScopeOps Platform
 * Provides graceful error handling at route, pane, and widget levels
 */

import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronLeft } from 'lucide-react';

/**
 * Base Error Boundary Component
 * Catches JavaScript errors in child component tree
 */
class BaseErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log to console in development
    console.error('[ErrorBoundary] Caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      context: this.props.context || 'unknown'
    });

    // TODO: Send to Sentry or equivalent when configured
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack, context: this.props.context } });
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback ? (
        this.props.fallback({ error: this.state.error, reset: this.handleReset })
      ) : (
        <DefaultErrorFallback 
          error={this.state.error}
          onReset={this.handleReset}
          variant={this.props.variant || 'default'}
          title={this.props.title}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * Default Error Fallback UI
 */
const DefaultErrorFallback = ({ error, onReset, variant = 'default', title }) => {
  const variants = {
    default: {
      container: 'bg-white rounded-xl border border-red-200 p-8',
      icon: 'w-12 h-12 text-red-500',
      title: 'text-xl font-semibold text-gray-900',
      message: 'text-gray-600'
    },
    compact: {
      container: 'bg-red-50 rounded-lg border border-red-200 p-4',
      icon: 'w-6 h-6 text-red-500',
      title: 'text-sm font-medium text-gray-900',
      message: 'text-xs text-gray-600'
    },
    inline: {
      container: 'bg-red-50/50 rounded-lg p-3 border border-red-100',
      icon: 'w-5 h-5 text-red-400',
      title: 'text-sm font-medium text-gray-800',
      message: 'text-xs text-gray-500'
    },
    pane: {
      container: 'h-full flex flex-col items-center justify-center p-6 bg-gray-50',
      icon: 'w-10 h-10 text-red-400',
      title: 'text-lg font-medium text-gray-900',
      message: 'text-sm text-gray-600'
    }
  };

  const style = variants[variant] || variants.default;

  return (
    <div className={style.container} data-testid="error-boundary-fallback">
      <div className={`flex ${variant === 'inline' ? 'flex-row items-center gap-3' : 'flex-col items-center text-center gap-4'}`}>
        <AlertTriangle className={style.icon} />
        <div>
          <h3 className={style.title}>{title || 'Something went wrong'}</h3>
          <p className={style.message}>
            {error?.message || 'An unexpected error occurred'}
          </p>
        </div>
        {onReset && (
          <button
            onClick={onReset}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Page-level Error Boundary
 * Use at route level for full-page error handling
 */
export const PageErrorBoundary = ({ children, pageName }) => (
  <BaseErrorBoundary 
    context={`page:${pageName}`}
    variant="default"
    title={`Error loading ${pageName || 'page'}`}
    fallback={({ error, reset }) => (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-xl border shadow-lg p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Page Error</h2>
          <p className="text-gray-600 mb-6">
            {error?.message || 'Failed to load this page. Please try again.'}
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </button>
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
            >
              <Home className="w-4 h-4" />
              Go Home
            </a>
          </div>
        </div>
      </div>
    )}
  >
    {children}
  </BaseErrorBoundary>
);

/**
 * Pane/Drawer Error Boundary
 * Use for side sheets and right panes
 */
export const PaneErrorBoundary = ({ children, paneName, onClose }) => (
  <BaseErrorBoundary 
    context={`pane:${paneName}`}
    variant="pane"
    title={`Error loading ${paneName || 'details'}`}
    fallback={({ error, reset }) => (
      <div className="h-full flex flex-col items-center justify-center p-6 bg-gray-50">
        <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load details</h3>
        <p className="text-sm text-gray-600 text-center mb-6 max-w-xs">
          {error?.message || 'There was a problem loading this content.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={reset}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Close
            </button>
          )}
        </div>
      </div>
    )}
  >
    {children}
  </BaseErrorBoundary>
);

/**
 * Widget Error Boundary  
 * Use for dashboard widgets and cards
 */
export const WidgetErrorBoundary = ({ children, widgetName }) => (
  <BaseErrorBoundary 
    context={`widget:${widgetName}`}
    variant="compact"
    title="Widget error"
  >
    {children}
  </BaseErrorBoundary>
);

/**
 * Inline Error Boundary
 * Use for small components that shouldn't break the whole page
 */
export const InlineErrorBoundary = ({ children, label }) => (
  <BaseErrorBoundary 
    context={`inline:${label}`}
    variant="inline"
    title={label || 'Error'}
  >
    {children}
  </BaseErrorBoundary>
);

export default BaseErrorBoundary;
